import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import { settingsAPI } from '../../api/api';
import { useApi } from '../../api/useApi';
import useIsMobile from '../../hooks/useIsMobile';

const FLASH_INTERVAL_MS = 4300;
const SHARED_SURFACE = '#eadcd7';
const SHARED_BUTTON_BG = '#0f1f3a';
const SHARED_MODAL_BG = '#f5e3ea';
const SHARED_MODAL_HEIGHT = 'min(760px, calc(100vh - 40px))';
const SHARED_HERO_HEIGHT = '420px';
const FLASH_SEEN_KEY = 'sedyam_feature_flash_seen';
const FLASH_SETTINGS_CACHE_KEY = 'sedyam_feature_flash_settings_cache';

const isExternalPath = (value) => /^(https?:)?\/\//i.test(String(value || '').trim());

const hasSeenFeatureFlash = () => {
  try {
    return sessionStorage.getItem(FLASH_SEEN_KEY) === '1';
  } catch {
    return false;
  }
};

const markFeatureFlashSeen = () => {
  try {
    sessionStorage.setItem(FLASH_SEEN_KEY, '1');
  } catch {
    // Ignore storage failures.
  }
};

const readStoredFeatureFlashSettings = () => {
  try {
    const raw = localStorage.getItem(FLASH_SETTINGS_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const cachedSettings = parsed?.settings && typeof parsed.settings === 'object' ? parsed.settings : parsed;
    return cachedSettings && typeof cachedSettings === 'object' ? cachedSettings : null;
  } catch {
    return null;
  }
};

const persistFeatureFlashSettings = (settings) => {
  try {
    localStorage.setItem(
      FLASH_SETTINGS_CACHE_KEY,
      JSON.stringify({
        cachedAt: Date.now(),
        settings,
      })
    );
  } catch {
    // Ignore storage failures.
  }
};

const mapSettingsToFlashItems = (slides = []) =>
  slides
    .filter((slide) => slide && slide.active !== false)
    .map((slide, index) => ({
      id: String(slide._id || slide.id || `flash-slide-${index}`),
      eyebrow: String(slide.eyebrow || "SEDYAM'S").trim(),
      title: String(slide.title || '').trim(),
      subtitle: String(slide.subtitle || '').trim(),
      path: String(slide.path || '/').trim(),
      image: String(slide.image || '').trim(),
      buttonLabel: String(slide.buttonLabel || 'Open Now').trim(),
    }))
    .filter((slide) => slide.title && slide.image);

const modalBackdrop = {
  position: 'fixed',
  inset: 0,
  zIndex: 2000,
  background: 'rgba(0, 0, 0, 0.58)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

export default function FeatureFlashSlider() {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(() => !hasSeenFeatureFlash());
  const [cachedSettings, setCachedSettings] = useState(() => readStoredFeatureFlashSettings());
  const [activeIndex, setActiveIndex] = useState(0);
  const { data: featureFlashSettings, loading: featureFlashLoading } = useApi(settingsAPI.getFeatureFlash, null, {
    cacheKey: 'feature-flash-settings',
    staleTime: 30000,
  });
  const resolvedSettings = featureFlashSettings || cachedSettings;

  useEffect(() => {
    if (!featureFlashSettings) return;
    persistFeatureFlashSettings(featureFlashSettings);
    setCachedSettings(featureFlashSettings);
  }, [featureFlashSettings]);

  const flashItems = useMemo(
    () => mapSettingsToFlashItems(Array.isArray(resolvedSettings?.slides) ? resolvedSettings.slides : []),
    [resolvedSettings?.slides]
  );

  const isFeatureFlashEnabled = resolvedSettings?.enabled !== false;

  useEffect(() => {
    if (!isOpen || flashItems.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % flashItems.length);
    }, FLASH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [flashItems.length, isOpen]);

  useEffect(() => {
    if (activeIndex < flashItems.length) return;
    setActiveIndex(0);
  }, [activeIndex, flashItems.length]);

  const activeItem = useMemo(() => flashItems[activeIndex] || flashItems[0], [activeIndex, flashItems]);

  const handleClose = () => {
    setIsOpen(false);
    markFeatureFlashSeen();
    if (location.pathname !== '/') {
      if (window.history.length > 1) navigate(-1);
      else navigate('/');
    }
  };

  const goPrev = () => setActiveIndex((prev) => (prev - 1 + flashItems.length) % flashItems.length);
  const goNext = () => setActiveIndex((prev) => (prev + 1) % flashItems.length);

  const openPath = (path) => {
    setIsOpen(false);
    markFeatureFlashSeen();
    if (isExternalPath(path)) {
      window.location.href = path;
      return;
    }
    navigate(path);
  };

  const modalHeight = isMobile ? 'calc(100vh - 12px)' : SHARED_MODAL_HEIGHT;
  const showLoadingOverlay = isOpen && featureFlashLoading && !resolvedSettings;

  if (showLoadingOverlay) {
    return (
      <div
        style={{
          ...modalBackdrop,
          padding: isMobile ? '6px' : '20px',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Loading feature highlights"
      >
        <div
          style={{
            width: isMobile ? '100%' : 'min(720px, 100%)',
            minHeight: isMobile ? '280px' : '340px',
            borderRadius: isMobile ? '14px' : '20px',
            background: SHARED_MODAL_BG,
            boxShadow: '0 28px 72px rgba(0,0,0,0.35)',
            border: '1px solid rgba(255,255,255,0.4)',
            display: 'grid',
            placeItems: 'center',
            padding: isMobile ? '24px' : '36px',
            textAlign: 'center',
          }}
        >
          <div>
            <div
              style={{
                width: '56px',
                height: '56px',
                margin: '0 auto 18px',
                borderRadius: '999px',
                border: '4px solid rgba(15,31,58,0.14)',
                borderTopColor: SHARED_BUTTON_BG,
                animation: 'feature-flash-spin 0.85s linear infinite',
              }}
            />
            <p style={{ fontSize: isMobile ? '1.05rem' : '1.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '8px' }}>
              Preparing featured highlights
            </p>
            <p style={{ fontSize: '0.95rem', color: '#475569', maxWidth: '360px' }}>
              Loading the latest flash page content before the homepage opens.
            </p>
          </div>
          <style>
            {`
              @keyframes feature-flash-spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  if (!isFeatureFlashEnabled || !isOpen || flashItems.length === 0 || !activeItem) return null;

  return (
    <div
      style={{
        ...modalBackdrop,
        padding: isMobile ? '6px' : '20px',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Feature highlights"
    >
      <div
        style={{
          position: 'relative',
          width: isMobile ? '100%' : 'min(1280px, 100%)',
          height: modalHeight,
          borderRadius: isMobile ? '14px' : '20px',
          background: SHARED_MODAL_BG,
          boxShadow: '0 28px 72px rgba(0,0,0,0.35)',
          border: '1px solid rgba(255,255,255,0.4)',
          maxHeight: '70vh',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'relative', height: '100%', minHeight: isMobile ? '100%' : SHARED_HERO_HEIGHT }}>
          <button
            onClick={handleClose}
            aria-label="Close highlights"
            style={{
              position: 'absolute',
              top: isMobile ? '10px' : '14px',
              right: isMobile ? '10px' : '14px',
              width: isMobile ? '34px' : '40px',
              height: isMobile ? '34px' : '40px',
              borderRadius: '999px',
              border: 'none',
              background: 'rgba(255,255,255,0.9)',
              color: '#111827',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              zIndex: 3,
            }}
          >
            <FiX size={20} />
          </button>

          <button
            onClick={goPrev}
            aria-label="Previous slide"
            style={{
              position: 'absolute',
              left: isMobile ? '8px' : '14px',
              top: isMobile ? '170px' : '50%',
              transform: isMobile ? 'none' : 'translateY(-50%)',
              width: isMobile ? '38px' : '46px',
              height: isMobile ? '38px' : '46px',
              borderRadius: '999px',
              border: 'none',
              background: 'rgba(255,255,255,0.86)',
              color: '#111827',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              zIndex: 3,
            }}
          >
            <FiChevronLeft size={28} />
          </button>

          <button
            onClick={goNext}
            aria-label="Next slide"
            style={{
              position: 'absolute',
              right: isMobile ? '8px' : '14px',
              top: isMobile ? '170px' : '50%',
              transform: isMobile ? 'none' : 'translateY(-50%)',
              width: isMobile ? '38px' : '46px',
              height: isMobile ? '38px' : '46px',
              borderRadius: '999px',
              border: 'none',
              background: 'rgba(255,255,255,0.86)',
              color: '#111827',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              zIndex: 3,
            }}
          >
            <FiChevronRight size={28} />
          </button>

          <div
            onClick={() => openPath(activeItem.path)}
            style={{
              cursor: 'pointer',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '52% 48%',
              alignItems: 'stretch',
              height: '100%',
            }}
          >
            <div
              style={{
                padding: isMobile ? '52px 18px 24px' : '52px 52px 40px 76px',
                background: SHARED_SURFACE,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
              }}
            >
              <p style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '1.2px', color: '#163250', marginBottom: '16px' }}>
                {activeItem.eyebrow || "SEDYAM'S"}
              </p>
              <h2
                style={{
                  fontSize: isMobile ? '1.7rem' : 'clamp(2.2rem, 3.4vw, 3rem)',
                  fontWeight: 800,
                  color: '#0f172a',
                  marginBottom: '12px',
                  lineHeight: 1.06,
                }}
              >
                {activeItem.title}
              </h2>
              <p
                style={{
                  fontSize: isMobile ? '0.95rem' : '1.05rem',
                  color: '#0f172a',
                  lineHeight: 1.55,
                  marginBottom: '28px',
                  maxWidth: '500px',
                }}
              >
                {activeItem.subtitle}
              </p>
              <button
                onClick={(event) => {
                  event.stopPropagation();
                  openPath(activeItem.path);
                }}
                style={{
                  border: 'none',
                  borderRadius: '999px',
                  padding: isMobile ? '10px 20px' : '13px 30px',
                  fontWeight: 700,
                  fontSize: isMobile ? '0.95rem' : '1.05rem',
                  cursor: 'pointer',
                  color: '#ffffff',
                  background: SHARED_BUTTON_BG,
                  alignSelf: 'flex-start',
                }}
              >
                {activeItem.buttonLabel || 'Open Now'}
              </button>
            </div>

            <div style={{ height: isMobile ? '180px' : '100%', background: '#fce7f3' }}>
              <img
                src={activeItem.image}
                alt={activeItem.title}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          </div>
        </div>

        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: isMobile ? '10px' : '18px',
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            zIndex: 4,
          }}
        >
          {flashItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => setActiveIndex(index)}
              aria-label={`Go to ${item.id}`}
              style={{
                width: index === activeIndex ? '24px' : '10px',
                height: '10px',
                borderRadius: '999px',
                border: 'none',
                background: index === activeIndex ? '#111827' : '#cbd5e1',
                cursor: 'pointer',
                transition: 'all 200ms ease',
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
